import { hasOnlyIncompleteTariffs } from '@billing/validation/incompleteClaim';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  ThemeIcon,
  useComputedColorScheme,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconCirclePlus,
  IconEdit,
  IconFileText,
  IconStethoscope,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import { formatPatientName } from '@billing/validation';
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { BulkClaimForm, IndividualBulkClaimForm, Payor } from 'interfaces';
import { ICD9Tooltip } from 'components/common/ICD9Badge';
import { useActiveForm } from 'pages/claims/components';
import { useCurrentSiteStore } from 'store/SiteStore';
import { buttonTextColor } from 'utils/buttonUtils';
import { formatDateOnly, formatTimeOnly, isFutureDate } from 'utils/dateUtils';
import { sumTariffTotals } from 'utils/tariffUtils';

interface BulkClaimPreviewSectionProps {
  handlePatientPrev: () => void;
  handleSubmitAll: () => void;
  payors: Payor[];
  isLoading: boolean;
  claims: IndividualBulkClaimForm[];
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  setSubmitClaims: Dispatch<SetStateAction<boolean>>;
  setEditingFromPreview: Dispatch<SetStateAction<boolean>>;
  setShowErrors: Dispatch<SetStateAction<boolean>>;
  isEvaluating?: boolean;
  hasCurrentBlockingRules?: boolean;
}

type ReviewFilter = 'all' | 'issues';

interface ClaimValidationIssue {
  field: string;
  message: React.ReactNode;
}

type ClaimReviewState = 'ready' | 'incomplete' | 'issues';

const getClaimReviewState = (
  claim: IndividualBulkClaimForm,
  issues: ClaimValidationIssue[]
): ClaimReviewState => {
  if (issues.length > 0) return 'issues';
  if (hasOnlyIncompleteTariffs(claim)) return 'incomplete';
  return 'ready';
};

const reviewAppearance = {
  ready: { color: 'teal', Icon: IconCheck },
  incomplete: { color: 'gray', Icon: IconFileText },
  issues: { color: 'red', Icon: IconAlertCircle },
};

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(value);

const getPatientName = (claim: IndividualBulkClaimForm): string =>
  claim.patient
    ? formatPatientName(claim.patient.firstName, claim.patient.lastName)
    : 'New Patient';

const getProviderName = (claim: IndividualBulkClaimForm): string => {
  if (!claim.provider) return 'N/A';
  return (
    `${claim.provider.firstName ?? ''} ${
      claim.provider.lastName ?? ''
    }`.trim() || 'N/A'
  );
};

const formatDisplayRange = (
  start: string,
  end: string,
  fallback = 'N/A'
): string => {
  if (start && end) return start === end ? start : `${start} – ${end}`;
  return start || end || fallback;
};

const getServicePeriodLabels = (
  claim: IndividualBulkClaimForm,
  timezone?: string | null
): { date: string; time: string } => {
  const dateOptions = { timezone, fallback: '' };
  const startDate = formatDateOnly(claim.serviceStart, dateOptions);
  const endDate = formatDateOnly(claim.serviceEnd, dateOptions);
  const startTime = formatTimeOnly(claim.serviceStart, dateOptions);
  const endTime = formatTimeOnly(claim.serviceEnd, {
    ...dateOptions,
    showTimeZone: true,
  });

  const time =
    startTime && endTime
      ? `${startTime} – ${endTime}`
      : formatTimeOnly(startTime ? claim.serviceStart : claim.serviceEnd, {
          timezone,
          fallback: 'N/A',
          showTimeZone: true,
        });

  return {
    date: formatDisplayRange(startDate, endDate),
    time,
  };
};

const getIssueField = (
  path: string,
  translate: (key: string, fallback?: string) => string
) => {
  if (path.includes('.tariffs.') && path.endsWith('.icd9')) {
    return translate('pages:claims.batchReview.fields.icd9', 'ICD-9');
  }

  const field = path.split('.')[2] ?? '';
  if (field === 'provider' || field === 'providerID') {
    return translate('pages:claims.batchReview.fields.provider', 'Provider');
  }
  if (field === 'patient' || field === 'patientID') {
    return translate('pages:claims.batchReview.fields.patient', 'Patient');
  }
  if (field === 'healthcard' || field === 'healthcardID') {
    return translate(
      'pages:claims.batchReview.fields.healthcard',
      'Health card'
    );
  }
  if (field === 'serviceStart' || field === 'serviceEnd') {
    return translate('pages:claims.batchReview.fields.date', 'Date');
  }
  if (field === 'tariffs') {
    return translate('pages:claims.batchReview.fields.tariff', 'Tariff');
  }
  return translate('pages:claims.batchReview.fields.claim', 'Claim');
};

const getClaimIssues = (
  form: UseFormReturnType<BulkClaimForm>,
  claim: IndividualBulkClaimForm,
  index: number,
  translate: (key: string, fallback?: string) => string
): ClaimValidationIssue[] => {
  const prefix = `claims.${index}`;
  const issues = Object.entries(form.errors)
    .filter(([path]) => path === prefix || path.startsWith(`${prefix}.`))
    .map(([path, message]) => ({
      field: getIssueField(path, translate),
      message,
    }));

  if (!claim.tariffs?.length) {
    issues.push({
      field: translate('pages:claims.batchReview.fields.tariff', 'Tariff'),
      message: translate(
        'pages:claims.batchReview.noTariffsError',
        'At least one tariff is required.'
      ),
    });
  }

  return issues;
};

export const BulkClaimPreviewSection: React.FC<
  BulkClaimPreviewSectionProps
> = ({
  handlePatientPrev,
  handleSubmitAll,
  isLoading,
  claims,
  setCurrentStep,
  setSubmitClaims,
  setEditingFromPreview,
  setShowErrors,
  isEvaluating = false,
  hasCurrentBlockingRules = false,
}) => {
  const { isBulk, form } = useActiveForm();
  const { timezone } = useCurrentSiteStore();
  const computedColorScheme = useComputedColorScheme('dark');
  const { translate } = useTranslation();
  const [filter, setFilter] = useState<ReviewFilter>('all');

  const handleDeleteClaim = (index: number) => {
    form.removeListItem('claims', index);
  };

  useEffect(() => {
    const scrubbedClaims = claims.filter(
      (claim) =>
        hasOnlyIncompleteTariffs(claim) ||
        (claim.patientID && claim.healthcardID)
    );
    form.setFieldValue('claims', scrubbedClaims);
  }, []);

  useEffect(() => {
    form.validate();
  }, [claims, form]);

  const claimIssues = useMemo(
    () =>
      claims.map((claim, index) =>
        getClaimIssues(form, claim, index, translate)
      ),
    [claims, form.errors, translate]
  );

  const reviewedClaims = claims.map((claim, index) => ({
    claim,
    index,
    issues: claimIssues[index],
    state: getClaimReviewState(claim, claimIssues[index]),
  }));
  const errorCount = reviewedClaims.filter(
    ({ state }) => state === 'issues'
  ).length;
  const readyCount = reviewedClaims.filter(
    ({ state }) => state === 'ready'
  ).length;
  const incompleteCount = reviewedClaims.filter(
    ({ state }) => state === 'incomplete'
  ).length;
  const visibleClaims = reviewedClaims.filter(
    ({ state }) => filter === 'all' || state === 'issues'
  );

  useEffect(() => {
    setShowErrors(errorCount > 0);
  }, [errorCount, setShowErrors]);

  if (!isBulk) return null;

  const hasFutureServiceStart = claims.some((claim) =>
    isFutureDate(claim.serviceStart, timezone)
  );
  const allIncomplete =
    claims.length > 0 && claims.every(hasOnlyIncompleteTariffs);
  const createDisabled =
    claims.length === 0 ||
    errorCount > 0 ||
    hasFutureServiceStart ||
    isLoading ||
    (!allIncomplete && (isEvaluating || hasCurrentBlockingRules));

  return (
    <Box maw={1200} mx="auto" pos="relative">
      <LoadingOverlay visible={isLoading} />
      <Stack gap="md">
        <Box>
          <Text size="xl" fw={700}>
            {translate('pages:claims.batchReview.title', 'Review batch claims')}
          </Text>
          <Text size="sm" c="dimmed" mt={2}>
            {translate(
              'pages:claims.batchReview.description',
              'Confirm the claims below before creating them.'
            )}
          </Text>
        </Box>

        <Paper
          withBorder
          radius="md"
          p="md"
          bg="var(--mantine-color-default-hover)"
        >
          <Flex
            justify="space-between"
            align={{ base: 'stretch', sm: 'center' }}
            direction={{ base: 'column', sm: 'row' }}
            gap="md"
          >
            <Group gap="xl">
              <Text size="sm" fw={700}>
                {translate('pages:claims.batchReview.claimCount', {
                  count: claims.length,
                  defaultValue:
                    claims.length === 1
                      ? '{{count}} claim'
                      : '{{count}} claims',
                })}
              </Text>
              <Group gap={6}>
                <IconCheck size={17} color="var(--mantine-color-teal-6)" />
                <Text size="sm" c="teal.7" fw={500}>
                  {translate('pages:claims.batchReview.readyCount', {
                    count: readyCount,
                    defaultValue: '{{count}} ready',
                  })}
                </Text>
              </Group>
              {incompleteCount > 0 && (
                <Group gap={6}>
                  <IconFileText size={17} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed" fw={500}>
                    {translate('pages:claims.batchReview.incompleteCount', {
                      count: incompleteCount,
                      defaultValue: '{{count}} incomplete',
                    })}
                  </Text>
                </Group>
              )}
              <Group gap={6}>
                <IconAlertCircle size={17} color="var(--mantine-color-red-6)" />
                <Text size="sm" c="red.7" fw={500}>
                  {translate('pages:claims.batchReview.errorCount', {
                    count: errorCount,
                    defaultValue: '{{count}} with errors',
                  })}
                </Text>
              </Group>
            </Group>
            <SegmentedControl
              value={filter}
              onChange={(value) => setFilter(value as ReviewFilter)}
              data={[
                {
                  value: 'all',
                  label: translate('pages:claims.batchReview.filters.all', {
                    count: claims.length,
                    defaultValue: 'All ({{count}})',
                  }),
                },
                {
                  value: 'issues',
                  label: translate('pages:claims.batchReview.filters.issues', {
                    count: errorCount,
                    defaultValue: 'Needs review ({{count}})',
                  }),
                },
              ]}
              radius="md"
              size="sm"
              w={{ base: '100%', sm: 260 }}
              styles={{
                root: {
                  backgroundColor: 'var(--mantine-color-default-hover)',
                  padding: 5,
                },
                indicator: {
                  backgroundColor: 'var(--mantine-color-body)',
                  border: '1px solid var(--mantine-color-default-border)',
                  boxShadow: 'var(--mantine-shadow-sm)',
                },
                label: {
                  fontWeight: 600,
                  paddingBlock: 7,
                },
              }}
            />
          </Flex>
        </Paper>

        {visibleClaims.length > 0 ? (
          <Stack gap="sm">
            {visibleClaims.map(({ claim, index, issues }) => (
              <ClaimReviewCard
                key={`${claim.patientID ?? 'new'}-${index}`}
                claim={claim}
                index={index}
                issues={issues}
                timezone={timezone}
                setCurrentStep={setCurrentStep}
                setSubmitClaims={setSubmitClaims}
                setEditingFromPreview={setEditingFromPreview}
                handleDeleteClaim={handleDeleteClaim}
              />
            ))}
          </Stack>
        ) : (
          <Paper withBorder radius="md" p="xl">
            <Text ta="center" size="sm" c="dimmed">
              {claims.length === 0
                ? translate(
                    'pages:claims.batchReview.empty',
                    'No claims to create.'
                  )
                : translate(
                    'pages:claims.batchReview.noIssues',
                    'No claims need review.'
                  )}
            </Text>
          </Paper>
        )}

        <Divider />
        <Flex
          justify="space-between"
          align={{ base: 'stretch', sm: 'center' }}
          direction={{ base: 'column', sm: 'row' }}
          gap="md"
        >
          <Text size="sm" c={errorCount > 0 ? 'red.7' : 'dimmed'}>
            {errorCount > 0
              ? translate('pages:claims.batchReview.resolveErrors', {
                  count: errorCount,
                  defaultValue:
                    errorCount === 1
                      ? 'Resolve {{count}} claim with errors to continue.'
                      : 'Resolve {{count}} claims with errors to continue.',
                })
              : translate('pages:claims.batchReview.batchTotal', {
                  total: formatCurrency(
                    claims.reduce(
                      (total, claim) =>
                        total + sumTariffTotals(claim.tariffs ?? []),
                      0
                    )
                  ),
                  defaultValue: 'Batch total: {{total}}',
                })}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setSubmitClaims(false);
                handlePatientPrev();
              }}
            >
              {translate('pages:claims.batchReview.back', 'Back to last claim')}
            </Button>
            <Button
              c={buttonTextColor(computedColorScheme, createDisabled)}
              color="green"
              onClick={handleSubmitAll}
              leftSection={<IconCirclePlus size={17} />}
              disabled={createDisabled}
              loading={isLoading}
            >
              {translate('pages:claims.batchReview.create', {
                count: claims.length,
                defaultValue:
                  claims.length === 1
                    ? 'Create {{count}} claim'
                    : 'Create {{count}} claims',
              })}
            </Button>
          </Group>
        </Flex>
      </Stack>
    </Box>
  );
};

interface ClaimReviewCardProps {
  claim: IndividualBulkClaimForm;
  index: number;
  issues: ClaimValidationIssue[];
  timezone?: string | null;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  setSubmitClaims: Dispatch<SetStateAction<boolean>>;
  setEditingFromPreview: Dispatch<SetStateAction<boolean>>;
  handleDeleteClaim: (index: number) => void;
}

const ClaimReviewCard: React.FC<ClaimReviewCardProps> = ({
  claim,
  index,
  issues,
  timezone,
  setCurrentStep,
  setSubmitClaims,
  setEditingFromPreview,
  handleDeleteClaim,
}) => {
  const { translate } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [deleteOpened, deleteModal] = useDisclosure(false);
  const patientName = getPatientName(claim);
  const providerName = getProviderName(claim);
  const hasErrors = issues.length > 0;
  const { color, Icon } = reviewAppearance[getClaimReviewState(claim, issues)];
  const tariffTotal = sumTariffTotals(claim.tariffs ?? []);
  const icd9Codes = Array.from(
    new Set((claim.tariffs ?? []).map((tariff) => tariff.icd9).filter(Boolean))
  ) as string[];
  const issueFields = Array.from(new Set(issues.map((issue) => issue.field)));
  const servicePeriod = getServicePeriodLabels(claim, timezone);
  const hasComment = Boolean(claim.internalComments?.trim());

  return (
    <Paper
      withBorder
      radius="md"
      style={{
        borderColor: hasErrors ? 'var(--mantine-color-red-4)' : undefined,
      }}
    >
      <Stack gap={0}>
        <Flex
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap="md"
          p="md"
        >
          <Group
            wrap="nowrap"
            align="flex-start"
            style={{ flex: 1, minWidth: 0 }}
          >
            <ThemeIcon variant="light" color={color} radius="xl" size="lg">
              <Icon size={18} />
            </ThemeIcon>
            <Box style={{ minWidth: 0 }}>
              <Group gap="xs">
                <Text size="xs" c="dimmed" fw={600}>
                  #{index + 1}
                </Text>
                <Text fw={700}>{patientName}</Text>
                {hasOnlyIncompleteTariffs(claim) && (
                  <Badge color="gray" variant="light">
                    {translate(
                      'pages:claims.batchReview.incomplete',
                      'Incomplete'
                    )}
                  </Badge>
                )}
                {hasComment && (
                  <Badge color="blue" variant="light">
                    {translate('pages:claims.batchReview.comment', 'Comment')}
                  </Badge>
                )}
              </Group>
              <Group gap="md" mt={4}>
                <Group gap={4} wrap="nowrap">
                  <IconStethoscope
                    size={15}
                    color="var(--mantine-color-dimmed)"
                  />
                  <Text size="xs" c="dimmed">
                    {providerName}
                  </Text>
                </Group>
                <Group gap={4} wrap="nowrap">
                  <IconFileText size={15} color="var(--mantine-color-dimmed)" />
                  {icd9Codes.length ? (
                    <Group gap={4} wrap="wrap">
                      {icd9Codes.map((icd9) => (
                        <ICD9Tooltip key={icd9} icd9={icd9}>
                          <Text size="xs" c="dimmed">
                            {icd9}
                          </Text>
                        </ICD9Tooltip>
                      ))}
                    </Group>
                  ) : (
                    <Text size="xs" c="dimmed">
                      N/A
                    </Text>
                  )}
                </Group>
                <Group gap={4} wrap="nowrap">
                  <IconCalendar
                    size={15}
                    color="var(--mantine-color-dimmed)"
                    style={{ flexShrink: 0 }}
                  />
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {servicePeriod.date} · {servicePeriod.time}
                  </Text>
                </Group>
              </Group>
            </Box>
          </Group>

          {issueFields.length > 0 && (
            <Group gap={6}>
              {issueFields.map((field) => (
                <Badge
                  key={field}
                  color="red"
                  variant="light"
                  leftSection={<IconAlertCircle size={12} />}
                >
                  {field}
                </Badge>
              ))}
            </Group>
          )}

          <Box ta={{ base: 'left', md: 'right' }} miw={115}>
            <Text fw={700} size="sm">
              {formatCurrency(tariffTotal)}
            </Text>
            <Text size="xs" c="dimmed">
              {translate('pages:claims.batchReview.tariffCount', {
                count: claim.tariffs?.length ?? 0,
                defaultValue:
                  claim.tariffs?.length === 1
                    ? '{{count}} tariff'
                    : '{{count}} tariffs',
              })}
            </Text>
          </Box>

          <Group gap={6} wrap="nowrap">
            <Button
              size="xs"
              variant="default"
              leftSection={<IconEdit size={15} />}
              onClick={() => {
                setSubmitClaims(false);
                setEditingFromPreview(true);
                setCurrentStep(index);
              }}
            >
              {translate('buttons.edit', 'Edit')}
            </Button>
            <ActionIcon
              variant="default"
              color="red"
              aria-label={translate('pages:claims.batchReview.deleteAria', {
                patient: patientName,
                defaultValue: 'Delete claim for {{patient}}',
              })}
              onClick={deleteModal.open}
            >
              <IconTrash size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              aria-label={
                expanded
                  ? translate(
                      'pages:claims.batchReview.collapse',
                      'Collapse claim details'
                    )
                  : translate(
                      'pages:claims.batchReview.expand',
                      'Expand claim details'
                    )
              }
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
            >
              <IconChevronDown
                size={18}
                style={{
                  transform: expanded ? 'rotate(180deg)' : undefined,
                  transition: 'transform 150ms ease',
                }}
              />
            </ActionIcon>
          </Group>
        </Flex>

        <Collapse in={expanded}>
          <Divider />
          <Stack gap="md" p="md">
            {issues.length > 0 && (
              <Stack gap={6}>
                {issues.map((issue, issueIndex) => (
                  <Group
                    key={`${issue.field}-${issueIndex}`}
                    gap="xs"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <IconAlertCircle
                      size={16}
                      color="var(--mantine-color-red-6)"
                      style={{ marginTop: 2, flexShrink: 0 }}
                    />
                    <Text size="sm">
                      <Text span fw={600}>
                        {issue.field}:
                      </Text>{' '}
                      {issue.message}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}

            {hasComment && (
              <Paper withBorder p="sm" radius="sm">
                <Text size="sm" fw={600} mb={4}>
                  {translate('pages:claims.batchReview.comment', 'Comment')}
                </Text>
                <Text
                  size="sm"
                  style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {claim.internalComments}
                </Text>
              </Paper>
            )}

            <ScrollArea>
              <Table striped highlightOnHover withTableBorder miw={620}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      {translate('pages:claims.batchReview.table.code', 'Code')}
                    </Table.Th>
                    <Table.Th>
                      {translate(
                        'pages:claims.batchReview.table.description',
                        'Description'
                      )}
                    </Table.Th>
                    <Table.Th>
                      {translate(
                        'pages:claims.batchReview.table.icd9',
                        'ICD-9'
                      )}
                    </Table.Th>
                    <Table.Th ta="right">
                      {translate(
                        'pages:claims.batchReview.table.units',
                        'Units'
                      )}
                    </Table.Th>
                    <Table.Th ta="right">
                      {translate(
                        'pages:claims.batchReview.table.amount',
                        'Amount'
                      )}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(claim.tariffs ?? []).map((tariff, tariffIndex) => (
                    <Table.Tr key={`${tariff.id}-${tariffIndex}`}>
                      <Table.Td>{`${tariff.prefix}${tariff.code}`}</Table.Td>
                      <Table.Td>{tariff.description || 'N/A'}</Table.Td>
                      <Table.Td>
                        {tariff.icd9 ? (
                          <ICD9Tooltip icd9={tariff.icd9}>
                            <Text size="sm">{tariff.icd9}</Text>
                          </ICD9Tooltip>
                        ) : (
                          'N/A'
                        )}
                      </Table.Td>
                      <Table.Td ta="right">{tariff.units ?? '—'}</Table.Td>
                      <Table.Td ta="right">
                        {formatCurrency(Number(tariff.total ?? 0))}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        </Collapse>
      </Stack>

      <Modal
        opened={deleteOpened}
        onClose={deleteModal.close}
        centered
        title={translate(
          'pages:claims.batchReview.deleteTitle',
          'Delete claim'
        )}
      >
        <Stack>
          <Text>
            {translate('pages:claims.batchReview.deleteConfirmation', {
              patient: patientName,
              defaultValue:
                "Are you sure you want to delete {{patient}}'s claim?",
            })}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={deleteModal.close}>
              {translate('buttons.cancel', 'Cancel')}
            </Button>
            <Button
              color="red"
              onClick={() => {
                handleDeleteClaim(index);
                deleteModal.close();
              }}
            >
              {translate('buttons.delete', 'Delete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};
